/**
 * 2026-06 · OverviewPhotoPicker — thumbnails from 产品一览表 whose item number
 * looks like this product's, plus a free-text search over the whole catalogue.
 * Clicking a thumbnail hands the photo URL back to the caller (used in the
 * 图片编辑 dialog to replace the product photo).
 */
import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL;

export function OverviewPhotoPicker({ itemNumber, value, onPick, disabled = false }) {
  const { t } = useLanguage();
  const [similar, setSimilar] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!itemNumber) { setSimilar([]); return; }
    let alive = true;
    api.get("/product-overview/similar", { params: { item_number: itemNumber, limit: 12 } })
      .then(({ data }) => { if (alive) setSimilar(data?.items || []); })
      .catch(() => { if (alive) setSimilar([]); });
    return () => { alive = false; };
  }, [itemNumber]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      api.get("/product-overview", { params: { search: q, page_size: 24, page: 1 } })
        .then(({ data }) => {
          if (!alive) return;
          setResults((data?.items || []).filter((r) => r.photo_url).map((r) => ({
            id: r.id, item_number: r.item_number || "", photo_url: r.photo_url, photo_type: r.photo_type || "",
          })));
        })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [query]);

  const list = query.trim().length >= 2 ? results : similar;

  return (
    <div className="space-y-1.5" data-testid="overview-photo-picker">
      <Label className="text-xs text-muted-foreground">
        {t("quote.editor.similarPhotos")} ({list.length})
      </Label>
      <div className="relative">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("quote.editor.searchOverviewPhotos")}
          className="h-8 text-xs pl-6"
          data-testid="overview-photo-search"
        />
      </div>
      {list.length === 0 ? (
        <p className="text-[10px] text-muted-foreground py-2">
          {searching ? t("common.loading") : t("quote.editor.noOverviewPhotos")}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-h-[260px] overflow-y-auto pr-0.5">
          {list.map((sp) => {
            const picked = value === sp.photo_url;
            return (
              <button
                key={sp.id}
                type="button"
                disabled={disabled}
                onClick={() => onPick(sp.photo_url)}
                title={`${sp.item_number}${sp.photo_type ? ` · ${sp.photo_type}` : ""}`}
                data-testid={`overview-photo-${sp.item_number}`}
                className={`relative aspect-square rounded-sm border overflow-hidden bg-white transition-all ${picked ? "border-brand ring-2 ring-brand/40" : "border-border hover:border-brand"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <img src={`${IMG_BASE}${sp.photo_url}`} alt={sp.item_number} className="w-full h-full object-contain" />
                <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[9px] font-mono truncate px-1">
                  {sp.item_number}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
